<script setup lang="ts">
import LogoIconSvg from '@/assets/images/logo-icon.svg';
import LogoTextSvg from '@/assets/images/logo-text.svg';
import { AppButton } from '~/components/base';
import AppLogo from '~/components/layout/AppLogo.vue';
import { useLayoutStore } from '~/stores/layout';

const {
  public: { appName },
} = useRuntimeConfig();
const layoutStore = useLayoutStore();

// Props
interface Props {
  mode?: string;
}

const { mode = 'default' } = defineProps<Props>();

const topbarMenuActive = ref(false);

const onTopBarMenuButton = () => {
  topbarMenuActive.value = !topbarMenuActive.value;
};

const onTopBarMenuClick = () => {
  topbarMenuActive.value = false;
};

const topbarButtonClass = computed(() => {
  return [
    'layout-topbar-menu-button layout-topbar-button',
    {
      'layout-topbar-menu-button-highlight': topbarMenuActive.value,
    },
  ];
});
</script>

<template>
  <!-- Default Layout -->
  <div v-if="mode === 'default'" class="layout-topbar">
    <NuxtLink to="/" class="layout-topbar-logo">
      <AppLogo />
      <span class="ml-2 text-xl font-semibold">{{ appName }}</span>
    </NuxtLink>

    <button
      ref="menubutton"
      type="button"
      class="layout-menu-button layout-topbar-button"
      @click="layoutStore.onMenuToggle"
    >
      <i class="pi pi-bars"></i>
    </button>

    <button
      ref="topbarmenubutton"
      type="button"
      :class="topbarButtonClass"
      @click="onTopBarMenuButton"
    >
      <i class="pi pi-ellipsis-v"></i>
    </button>

    <div
      ref="topbarmenu"
      class="layout-topbar-menu"
      :class="{ 'layout-topbar-menu-mobile-active': topbarMenuActive }"
      @click="onTopBarMenuClick"
    >
      <button type="button" class="layout-topbar-button">
        <i class="pi pi-user"></i>
        <span>Profile</span>
      </button>
      <button type="button" class="layout-topbar-button">
        <i class="pi pi-cog"></i>
        <span>Settings</span>
      </button>
    </div>
  </div>

  <!-- Guest Navigation -->
  <nav v-else-if="mode === 'guest'" class="guest-nav">
    <div class="nav-brand">
      <AppLogo />
      <span class="ml-2 text-xl font-bold text-white">{{ appName }}</span>
    </div>

    <div class="nav-menu">
      <AppButton
        label="Landings"
        text
        severity="secondary"
        class="text-white hover:text-blue-200"
      />
      <AppButton
        label="Secondary Pages"
        text
        severity="secondary"
        class="text-white hover:text-blue-200"
      />
      <AppButton
        label="Account Pages"
        text
        severity="secondary"
        class="text-white hover:text-blue-200"
      />
    </div>

    <!-- Mobile Menu Button -->
    <button
      v-show="false"
      class="mobile-menu-btn md:hidden"
      @click="topbarMenuActive = !topbarMenuActive"
    >
      <i class="pi pi-bars text-xl text-white"></i>
    </button>

    <div class="nav-actions">
      <AppButton
        label="Sign Up"
        class="signup-btn bg-white text-gray-900 hover:bg-gray-100"
      />
      <AppButton
        icon="pi pi-search"
        text
        severity="secondary"
        class="text-white hover:text-blue-200"
      />
    </div>
  </nav>

  <!-- Auth Navigation (Logo only) -->
  <header
    v-else-if="mode === 'auth'"
    class="auth-nav sticky top-0 z-50 bg-[#F1F5F9] pl-12 backdrop-blur transition-all duration-300"
  >
    <div class="flex h-20 items-center justify-start">
      <NuxtLink to="/" class="flex items-center gap-2 text-lg font-bold">
        <LogoIconSvg filled :font-controlled="false" class="h-6 w-6" />
        <LogoTextSvg
          color="#306294"
          :font-controlled="false"
          class="h-[12.28px] w-[48.97px]"
        />
      </NuxtLink>
    </div>
  </header>

  <header
    v-else-if="mode === 'simple'"
    class="auth-nav top-0 z-50 bg-[#F1F5F9] pl-4 backdrop-blur transition-all duration-300 xl:pl-16"
  >
    <div
      class="flex h-20 items-center justify-between pr-4 max-md:pr-4 xl:pr-16"
    >
      <NuxtLink to="/" class="flex items-center gap-2 text-lg font-bold">
        <LogoIconSvg filled :font-controlled="false" class="h-6 w-6" />
        <LogoTextSvg
          color="#306294"
          :font-controlled="false"
          class="h-[12.28px] w-[48.97px]"
        />
      </NuxtLink>
      <!-- Mobile: allow simple topbar consumers to inject right-side actions like a burger trigger -->
      <div class="flex items-center">
        <slot name="simple-actions"></slot>
      </div>
    </div>
  </header>
</template>

<style lang="scss" scoped>
/* Default Layout Styles */
.layout-topbar {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 40;
  display: flex;
  align-items: center;
  padding: 0.75rem 1.5rem;
  background-color: white;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  height: 5rem;
}

.layout-topbar-logo {
  display: flex;
  align-items: center;
  color: #111827;
  text-decoration: none;
}

.layout-topbar-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 9999px;
  color: #6b7280;
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.layout-topbar-button:hover {
  color: #111827;
  background-color: #f3f4f6;
}

.layout-menu-button {
  margin-right: auto;
  margin-left: 0.5rem;
}

.layout-topbar-menu-button {
  margin-left: auto;
}

.layout-topbar-menu-button-highlight {
  background-color: #f3f4f6;
  color: #111827;
}

.layout-topbar-menu {
  position: absolute;
  top: 100%;
  right: 1.5rem;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  padding: 0.5rem 0;
  min-width: 12rem;
  opacity: 0;
  visibility: hidden;
  transform: scale(0.95);
  transition: all 0.2s;
}

.layout-topbar-menu-mobile-active {
  opacity: 1;
  visibility: visible;
  transform: scale(1);
}

.layout-topbar-menu .layout-topbar-button {
  width: 100%;
  justify-content: flex-start;
  padding: 0.5rem 1rem;
  border-radius: 0;
  text-align: left;
}

.layout-topbar-menu .layout-topbar-button:hover {
  background-color: #f9fafb;
}

.layout-topbar-menu .layout-topbar-button span {
  margin-left: 0.5rem;
}

/* Guest Navigation Styles */
.guest-nav {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 2rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.nav-brand {
  display: flex;
  align-items: center;
  color: white;
  font-weight: bold;
  transition: all 0.3s ease;
}

.nav-brand:hover {
  transform: scale(1.05);
}

.nav-menu {
  display: flex;
  gap: 2rem;
  align-items: center;
}

.nav-menu :deep(.p-button) {
  color: white !important;
  transition: all 0.3s ease;
  border-radius: 8px;
  padding: 0.5rem 1rem;
}

.nav-menu :deep(.p-button:hover) {
  background: rgba(255, 255, 255, 0.1) !important;
  transform: translateY(-2px);
}

.nav-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.nav-actions :deep(.signup-btn) {
  background: rgba(255, 255, 255, 0.9) !important;
  color: #1f2937 !important;
  border-radius: 8px;
  padding: 0.5rem 1.5rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.nav-actions :deep(.signup-btn:hover) {
  background: white !important;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

.mobile-menu-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.mobile-menu-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Dark mode */
:global(.dark) .layout-topbar {
  background-color: #111827;
  border-bottom-color: #374151;
}

:global(.dark) .layout-topbar-logo {
  color: white;
}

:global(.dark) .layout-topbar-button {
  color: #d1d5db;
}

:global(.dark) .layout-topbar-button:hover {
  color: white;
  background-color: #1f2937;
}

:global(.dark) .layout-topbar-menu {
  background-color: #1f2937;
  border-color: #374151;
}

:global(.dark) .layout-topbar-menu .layout-topbar-button:hover {
  background-color: #374151;
}

:global(.dark) .layout-topbar-menu-button-highlight {
  background-color: #1f2937;
  color: white;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .nav-menu {
    display: none;
  }

  .guest-nav {
    padding: 1rem 1.5rem;
  }
}

@media (max-width: 768px) {
  .guest-nav {
    padding: 1rem;
  }

  .nav-actions {
    gap: 0.5rem;
  }

  .nav-actions :deep(.signup-btn) {
    padding: 0.4rem 1rem;
    font-size: 0.875rem;
  }

  .nav-brand span {
    display: none;
  }
}
</style>
